import { AutocompleteAdapter } from 'components/form/AutocompleteAdapter';
import ReactFinalForm from 'components/form/ReactFinalForm';
import { TextFieldAdapter } from 'components/form/TextFieldAdapter';
import { IOption, IStatusOption } from 'interfaces';
import { IdentityField } from 'components/form/IdentityField';
import { useTranslation } from 'react-i18next';
import {
  locationService,
  labelThresholdService,
  categoryService,
  productWarehouseService
} from 'services/api';
import { useState, useEffect } from 'react';
import {
  Typography,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle
} from '@mui/material';
import { MultipleAutocompleteAdapter } from 'components/form/MultipleAutocompleteAdapter';
import { ProductWarehouseStatus } from '../../../constants';

interface MiscFormParams {
  // initial values
  row: any;
  open: boolean;
  onClose: () => void;
  // refresher functions
  refresh?: React.RefObject<() => void>;
}

function MiscDialogForm({ row, open, onClose, refresh }: MiscFormParams) {
  const { t } = useTranslation();
  const translationPrefix = 'pages.warehouse.edit.misc_product_warehouse_form';

  const [categories, setCategories] = useState<IOption[]>([]);
  const [labelCodes, setLabelCodes] = useState<string[]>([]);
  const [locations, setLocations] = useState<IOption[]>([]);

  const statusOptions: IStatusOption[] = Object.keys(ProductWarehouseStatus)
    .filter((key) => isNaN(Number(key)))
    .map((key) => ({
      value: ProductWarehouseStatus[key as keyof typeof ProductWarehouseStatus],
      display_name: key
    }));

  useEffect(() => {
    // Fetch categories, status options, label options, and locations from the API
    labelThresholdService.getAllBaseInformation().then((res) => {
      setLabelCodes(res.map((opt) => opt.code));
    });
    categoryService.getAllBaseInformation().then(setCategories);
    locationService.getAllBaseInformation().then(setLocations);
  }, []);

  const handleSubmit = (values: any) => {
    console.log('Submitting values: ', values);
    const updatedValues = {
      ...values,
      product: row.product,
      warehouse: row.warehouse
    };
    productWarehouseService.updateThresholds([updatedValues]).then(() => {
      if (refresh && refresh.current) {
        refresh.current();
      }
      onClose();
    });
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
      <DialogTitle>{t('global.edit')}</DialogTitle>
      <DialogContent>
        <div>
          <Typography variant="h6" gutterBottom>
            {t(`${translationPrefix}.edit-threshold-title`, {
              product: row.product,
              warehouse: row.warehouse
            })}
          </Typography>
          <ReactFinalForm
            key={`${row.product}-${row.warehouse}-${open}-${categories.length}-${labelCodes.length}-${locations.length}`}
            onSubmit={handleSubmit}
            hideControls={true}
            formId="updateProductWarehouseThresholdsAndLocationForm">
            <h3 className="text-lg font-semibold">
              {t(`${translationPrefix}.thresholds-edit-section`)}
            </h3>
            <div className="grid gap-2 p-2">
              <div className="grid grid-cols-2 gap-2">
                <IdentityField
                  name="category"
                  label={t('global.category')}
                  component={AutocompleteAdapter}
                  initValue={categories.find((c) => c.code === row.category)}
                  options={categories}
                  optionValue="code"
                  placeholder="Category"
                />

                {statusOptions && (
                  <IdentityField
                    name="status"
                    label={t(`entities.product.status`)}
                    component={AutocompleteAdapter}
                    initValue={statusOptions.find((opt) => opt.display_name === row.status)}
                    options={statusOptions}
                    placeholder="Status"
                    type="text"
                    optionLabel="display_name"
                    optionValue="value"
                  />
                )}
              </div>
              <div className=" grid grid-cols-4 gap-2">
                <div className="">
                  <IdentityField
                    name="label"
                    component={AutocompleteAdapter}
                    options={labelCodes.map((code) => ({ code, display_name: '' }))}
                    initValue={
                      labelCodes.find((code) => code === row.label)
                        ? { code: row.label, display_name: '' }
                        : null
                    }
                    label={t('global.label')}
                    optionValue="code"
                  />
                </div>
                <div className="">
                  <IdentityField
                    name="understock_threshold"
                    component={TextFieldAdapter}
                    initialValue={row.understock_threshold}
                    label={t('entities.product.understock_threshold')}
                    type="number"
                  />
                </div>
                <div className="">
                  <IdentityField
                    name="overstock_threshold"
                    component={TextFieldAdapter}
                    initialValue={row.overstock_threshold}
                    label={t('entities.product.overstock_threshold')}
                    type="number"
                  />
                </div>
                <div className="">
                  <IdentityField
                    name="restock_threshold"
                    component={TextFieldAdapter}
                    label={t('entities.product.restock_threshold')}
                    initialValue={row.restock_threshold}
                    type="number"
                  />
                </div>
              </div>
            </div>
            <div className="w-full gap-2 p-2">
              <IdentityField
                name="notes"
                component={TextFieldAdapter}
                initialValue={row.notes}
                label={t('global.notes')}
                type="text"
              />
            </div>
            <br />
            <h3 className="text-lg font-semibold">
              {t(`${translationPrefix}.locations-edit-section`)}
            </h3>
            <div className="w-full mb-4 p-2">
              {locations.length > 0 && (
                <IdentityField
                  name="locations"
                  component={MultipleAutocompleteAdapter}
                  options={locations}
                  initValue={row.location?.map((locCode: string) =>
                    locations.find((s) => s.code === locCode)
                  )}
                  label={t('global.locations')}
                  type="text"
                />
              )}
            </div>
          </ReactFinalForm>
        </div>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>{t('actions.cancel')}</Button>
        <Button
          type="submit"
          form="updateProductWarehouseThresholdsAndLocationForm"
          variant="outlined">
          {t('actions.save')}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export default MiscDialogForm;
